import React, { useState, useRef } from 'react';
import { ALPHABET_DATA, LetterItem } from '../../core/curriculum/curriculumData';
import { speechProvider } from '../../core/speech/speechProvider';
import { appRepository } from '../../core/storage/repository';
import { RaraMascot } from '../../components/mascot/RaraMascot';
import confetti from 'canvas-confetti';
import { AlphabetIcon, BackIcon, SpeakerIcon, WritingIcon } from '../../components/icons/ReadingIcons';

interface AlphabetViewProps {
  onBack: () => void;
}

export const AlphabetView: React.FC<AlphabetViewProps> = ({ onBack }) => {
  const [selectedLetter, setSelectedLetter] = useState<LetterItem>(ALPHABET_DATA[0]);
  const [isTracingMode, setIsTracingMode] = useState(false);
  const [mascotMessage, setMascotMessage] = useState(`Sentuh tombol untuk dengar bunyi huruf ${ALPHABET_DATA[0].letter}!`);
  const [mascotState, setMascotState] = useState<'idle' | 'happy' | 'speaking' | 'encourage'>('idle');

  // Tracing Canvas state
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);

  // 1. Melafalkan HURUF SAJA: e.g. "N" atau "B"
  const playOnlyLetter = async (item: LetterItem) => {
    setMascotState('speaking');
    setMascotMessage(`Bunyi huruf: ${item.letter}`);
    await speechProvider.speak(item.letter);
    setMascotState('happy');
    appRepository.recordLetterLearned(item.letter);
  };

  // 2. Melafalkan SELURUH NARASI: e.g. "N! Seperti Nasi." atau "B! Seperti Bola."
  const playFullNarration = async (item: LetterItem) => {
    setMascotState('speaking');
    setMascotMessage(`${item.letter}! Seperti ${item.exampleWord}.`);
    await speechProvider.speak(`${item.letter}! Seperti ${item.exampleWord}.`);
    setMascotState('happy');
    appRepository.recordLetterLearned(item.letter);
  };

  // 3. Melafalkan CONTOH KATA SAJA: e.g. "Nasi", "Bola", atau "Ikan"
  const playOnlyWord = async (item: LetterItem) => {
    setMascotState('speaking');
    setMascotMessage(`Kata: ${item.exampleWord}`);
    await speechProvider.speak(item.exampleWord);
    setMascotState('happy');
  };

  const handleNext = () => {
    const idx = ALPHABET_DATA.findIndex(i => i.id === selectedLetter.id);
    const nextItem = ALPHABET_DATA[(idx + 1) % ALPHABET_DATA.length];
    setSelectedLetter(nextItem);
    clearCanvas();
    playOnlyLetter(nextItem);
  };

  const handlePrev = () => {
    const idx = ALPHABET_DATA.findIndex(i => i.id === selectedLetter.id);
    const prevItem = ALPHABET_DATA[(idx - 1 + ALPHABET_DATA.length) % ALPHABET_DATA.length];
    setSelectedLetter(prevItem);
    clearCanvas();
    playOnlyLetter(prevItem);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  // Tracing pointer handlers
  const startDrawing = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.lineWidth = 14;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#4F7CF7';
    const rect = canvas.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
    setIsDrawing(true);
  };

  const draw = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      setMascotState('happy');
      setMascotMessage('Hebat! Garismu bagus sekali!');
      confetti({ particleCount: 25, spread: 50, origin: { y: 0.6 } });
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-[#FFFDF8] pb-12">
      {/* Top Header */}
      <div className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b-2 border-amber-100 px-4 py-3 flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-2 bg-amber-100 hover:bg-amber-200 active:scale-95 text-amber-900 font-bold px-4 py-2.5 rounded-2xl transition"
        >
          <BackIcon className="w-6 h-6" />
          <span className="text-sm md:text-base">Kembali</span>
        </button>

        <h1 className="text-xl md:text-2xl font-display font-bold text-[#4F7CF7] flex items-center gap-2">
          <AlphabetIcon className="w-9 h-9" /> Belajar Huruf
        </h1>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsTracingMode(!isTracingMode)}
            className={`px-3.5 py-2 rounded-2xl font-bold text-xs md:text-sm transition-all ${
              isTracingMode
                ? 'bg-purple-600 text-white shadow-md'
                : 'bg-purple-50 text-purple-700 border-2 border-purple-200 hover:bg-purple-100'
            }`}
          >
            <WritingIcon className="w-5 h-5" /> {isTracingMode ? 'Lihat Kartu' : 'Tulis Huruf'}
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto w-full px-4 pt-4 flex-1 flex flex-col gap-5">
        {/* Mascot companion feedback */}
        <RaraMascot
          state={mascotState}
          message={mascotMessage}
          size="sm"
          onBubbleClick={() => playFullNarration(selectedLetter)}
        />

        {/* Dedicated Narration Banner: Sentuh untuk dengar "N! Seperti Nasi." */}
        <button
          onClick={() => playFullNarration(selectedLetter)}
          className="w-full bg-gradient-to-r from-amber-100 via-orange-100 to-amber-100 hover:from-amber-200 hover:to-orange-200 border-2 border-amber-300 px-4 py-3 rounded-2xl flex items-center justify-between text-left shadow-xs transition active:scale-98 cursor-pointer"
          title="Sentuh untuk melafalkan semua: narasi huruf dan contoh kata"
        >
          <div className="flex items-center gap-3">
            <SpeakerIcon className="w-8 h-8 shrink-0" />
            <div>
              <div className="text-xs font-bold text-amber-800 uppercase tracking-wider">
                Narasi Lengkap (Sentuh untuk Suara Semua):
              </div>
              <div className="font-display font-black text-[#243047] text-base md:text-lg">
                "{selectedLetter.letter}! Seperti {selectedLetter.exampleWord}."
              </div>
            </div>
          </div>
          <span className="text-xs font-extrabold text-amber-900 bg-white/90 px-3 py-1.5 rounded-xl shadow-2xs shrink-0">
            Dengar Semua ▶
          </span>
        </button>

        {/* Big Letter Learning Stage */}
        <div className="bg-white rounded-3xl p-6 shadow-xl border-4 border-amber-200 flex flex-col items-center justify-center relative overflow-hidden">
          {/* Letter Navigation Arrows */}
          <button
            onClick={handlePrev}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 bg-amber-50 hover:bg-amber-100 active:scale-90 border-2 border-amber-300 rounded-full flex items-center justify-center text-xl font-bold text-amber-800 shadow cursor-pointer z-10"
            title="Huruf Sebelumnya"
          >
            ◀
          </button>
          <button
            onClick={handleNext}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 bg-amber-50 hover:bg-amber-100 active:scale-90 border-2 border-amber-300 rounded-full flex items-center justify-center text-xl font-bold text-amber-800 shadow cursor-pointer z-10"
            title="Huruf Selanjutnya"
          >
            ▶
          </button>

          {!isTracingMode ? (
            /* Standard Card View */
            <div className="flex flex-col items-center text-center my-2 max-w-md w-full">
              {/* Big Letters: Sentuh untuk membunyikan huruf itu saja */}
              <div className="flex items-baseline justify-center gap-4 md:gap-8 mb-3">
                <span
                  className="text-7xl md:text-9xl font-black font-display tracking-tight transition-transform hover:scale-105 active:scale-95 cursor-pointer select-none"
                  style={{ color: selectedLetter.color }}
                  onClick={() => playOnlyLetter(selectedLetter)}
                  title={`Sentuh untuk bunyikan huruf ${selectedLetter.letter}`}
                >
                  {selectedLetter.letter}
                </span>
                <span
                  className="text-5xl md:text-7xl font-extrabold text-[#64748B] transition-transform hover:scale-105 active:scale-95 cursor-pointer select-none"
                  onClick={() => playOnlyLetter(selectedLetter)}
                  title={`Sentuh untuk bunyikan huruf ${selectedLetter.lowercase}`}
                >
                  {selectedLetter.lowercase}
                </span>
              </div>

              {/* 1. Tombol Bunyikan Huruf N: Melafalkan huruf itu saja */}
              <button
                onClick={() => playOnlyLetter(selectedLetter)}
                className="flex items-center justify-center gap-3 bg-[#4F7CF7] hover:bg-blue-600 active:scale-95 text-white font-bold text-base md:text-lg px-7 py-3 rounded-full shadow-lg transition-all cursor-pointer w-full sm:w-auto"
                title={`Bunyikan huruf ${selectedLetter.letter} saja`}
              >
                <SpeakerIcon className="w-6 h-6" />
                <span>Bunyikan Huruf {selectedLetter.letter}</span>
              </button>

              {/* 2. Contoh Kata: Sentuh untuk melafalkan kata itu saja (misal: "Nasi" atau "Ikan") */}
              <div
                onClick={() => playOnlyWord(selectedLetter)}
                className="mt-6 w-full bg-amber-50 hover:bg-amber-100 active:scale-98 cursor-pointer transition border-2 border-amber-200 rounded-2xl px-5 py-4 flex items-center justify-between shadow-xs"
                title={`Sentuh untuk bunyikan kata "${selectedLetter.exampleWord}" saja`}
              >
                <div className="flex items-center gap-4">
                  <span className="text-4xl md:text-5xl">{selectedLetter.emoji}</span>
                  <div className="text-left">
                    <div className="text-xs font-bold text-amber-800 uppercase tracking-wider flex items-center gap-1.5">
                      <span>Contoh Kata</span>
                    </div>
                    <div className="text-2xl md:text-3xl font-display font-extrabold text-[#243047]">
                      <span className="underline decoration-amber-400 decoration-4">{selectedLetter.letter}</span>
                      {selectedLetter.exampleWord.slice(1)}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-1.5 text-amber-900 bg-amber-200/70 px-3 py-1.5 rounded-xl text-xs font-bold shrink-0">
                  <SpeakerIcon className="w-5 h-5" />
                  <span>Bunyikan Kata</span>
                </div>
              </div>
            </div>
          ) : (
            /* Interactive Tracing / Writing Canvas */
            <div className="flex flex-col items-center w-full my-2">
              <div className="text-sm font-bold text-purple-700 mb-2">
                Ayo ikuti bentuk huruf <strong>{selectedLetter.letter}</strong> dengan jarimu!
              </div>

              <div className="relative w-64 h-64 md:w-80 md:h-80 bg-purple-50 rounded-2xl border-4 border-dashed border-purple-300 flex items-center justify-center overflow-hidden touch-none">
                {/* Guide Letter in background */}
                <span className="absolute text-8xl md:text-9xl font-black text-purple-200 select-none pointer-events-none">
                  {selectedLetter.letter}
                </span>

                <canvas
                  ref={canvasRef}
                  width={320}
                  height={320}
                  className="w-full h-full cursor-crosshair relative z-10"
                  onPointerDown={startDrawing}
                  onPointerMove={draw}
                  onPointerUp={stopDrawing}
                  onPointerLeave={stopDrawing}
                />
              </div>

              <div className="flex items-center gap-4 mt-4">
                <button
                  onClick={clearCanvas}
                  className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-2xl text-sm cursor-pointer"
                >
                  Hapus Garis
                </button>
                <button
                  onClick={() => playOnlyLetter(selectedLetter)}
                  className="px-5 py-2.5 bg-[#4F7CF7] text-white font-bold rounded-2xl text-sm shadow hover:bg-blue-600 cursor-pointer"
                >
                  🔊 Bunyikan Huruf {selectedLetter.letter}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* 26 Letters Grid Picker */}
        <div>
          <h2 className="text-base font-bold text-[#64748B] mb-3 flex items-center gap-2">
            <span>Pilih Huruf A sampai Z:</span>
          </h2>
          <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-9 gap-2 md:gap-3">
            {ALPHABET_DATA.map(item => {
              const isSelected = item.id === selectedLetter.id;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    setSelectedLetter(item);
                    clearCanvas();
                    playOnlyLetter(item);
                  }}
                  className={`h-14 md:h-16 rounded-2xl font-display font-extrabold text-xl md:text-2xl transition-all flex items-center justify-center shadow-xs active:scale-90 cursor-pointer ${
                    isSelected
                      ? 'bg-[#4F7CF7] text-white scale-105 shadow-md ring-4 ring-blue-200'
                      : 'bg-white border-2 border-amber-200 text-[#243047] hover:bg-amber-50'
                  }`}
                >
                  {item.letter}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
